import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { OpportunityChatMessage, OpportunityChatThread } from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_OPPORTUNITY_CHAT_MESSAGES,
  COLLECTION_OPPORTUNITY_CHAT_THREADS,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { newId } from '../utils/id';

/**
 * Synthetic thread id for messages written before opportunity chats had
 * threads. Contains `__` on purpose so it can never collide with a real
 * thread id (real ids must not contain `__`).
 */
export const OPPORTUNITY_LEGACY_THREAD_ID = '__legacy__';

const LEGACY_THREAD_TITLE = 'Earlier conversation';
const FIRESTORE_BATCH_LIMIT = 500;

export type OpportunityChatThreadsState = {
  /** Recent-first; includes a synthetic "Earlier conversation" thread when pre-thread messages exist. */
  threads: OpportunityChatThread[];
  /** Chronological messages of the active thread only. */
  messages: OpportunityChatMessage[];
  threadsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  createThread: (title?: string) => Promise<OpportunityChatThread>;
  deleteThread: (threadId: string) => Promise<void>;
};

function docToMessage(d: QueryDocumentSnapshot): OpportunityChatMessage {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    opportunityId: typeof x.opportunityId === 'string' ? x.opportunityId : '',
    threadId: typeof x.threadId === 'string' && x.threadId.trim() ? x.threadId.trim() : undefined,
    role: x.role === 'assistant' ? 'assistant' : 'user',
    content: typeof x.content === 'string' ? x.content : '',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

function docToThread(d: QueryDocumentSnapshot): OpportunityChatThread {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    opportunityId: typeof x.opportunityId === 'string' ? x.opportunityId : '',
    title: typeof x.title === 'string' && x.title.trim() ? x.title.trim() : 'New chat',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

/** Oldest messages first. */
export function sortOpportunityChatChronological(
  messages: OpportunityChatMessage[]
): OpportunityChatMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Most recently updated threads first. */
export function sortOpportunityChatThreadsRecentFirst(
  threads: OpportunityChatThread[]
): OpportunityChatThread[] {
  return [...threads].sort((a, b) => {
    const byUpdated = b.updatedAt.localeCompare(a.updatedAt);
    if (byUpdated !== 0) return byUpdated;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** Builds the synthetic sidebar entry for pre-thread messages, or null when none exist. */
export function buildLegacyOpportunityThread(
  userId: string,
  opportunityId: string,
  messages: OpportunityChatMessage[]
): OpportunityChatThread | null {
  const legacyMessages = messages.filter((message) => !message.threadId);
  if (legacyMessages.length === 0) return null;

  const sorted = sortOpportunityChatChronological(legacyMessages);
  return {
    id: OPPORTUNITY_LEGACY_THREAD_ID,
    userId,
    opportunityId,
    title: LEGACY_THREAD_TITLE,
    createdAt: sorted[0].createdAt,
    updatedAt: sorted[sorted.length - 1].createdAt,
  };
}

/** Messages belonging to one thread; the legacy id selects pre-thread messages. */
export function filterOpportunityThreadMessages(
  messages: OpportunityChatMessage[],
  threadId: string | null
): OpportunityChatMessage[] {
  if (!threadId) return [];
  if (threadId === OPPORTUNITY_LEGACY_THREAD_ID) {
    return messages.filter((message) => !message.threadId);
  }
  return messages.filter((message) => message.threadId === threadId);
}

/**
 * Live threads + messages for one opportunity. Messages are written
 * server-side by the callable; threads are created and deleted by the client.
 */
export function useOpportunityChatThreads(
  userId: string,
  opportunityId: string,
  activeThreadId: string | null
): OpportunityChatThreadsState {
  const [storedThreads, setStoredThreads] = useState<OpportunityChatThread[]>([]);
  const [allMessages, setAllMessages] = useState<OpportunityChatMessage[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(Boolean(userId && opportunityId));
  const [messagesLoading, setMessagesLoading] = useState(Boolean(userId && opportunityId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !opportunityId) {
      setStoredThreads([]);
      setThreadsLoading(false);
      setError(null);
      return;
    }

    setThreadsLoading(true);

    const threadsQuery = query(
      collection(db, COLLECTION_OPPORTUNITY_CHAT_THREADS),
      where('userId', '==', userId),
      where('opportunityId', '==', opportunityId)
    );

    const unsubscribe = onSnapshot(
      threadsQuery,
      (snap) => {
        setStoredThreads(snap.docs.map(docToThread));
        setThreadsLoading(false);
      },
      (e) => {
        setError(e.message);
        setThreadsLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, opportunityId]);

  useEffect(() => {
    if (!userId || !opportunityId) {
      setAllMessages([]);
      setMessagesLoading(false);
      return;
    }

    setMessagesLoading(true);

    const messagesQuery = query(
      collection(db, COLLECTION_OPPORTUNITY_CHAT_MESSAGES),
      where('userId', '==', userId),
      where('opportunityId', '==', opportunityId)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snap) => {
        setAllMessages(snap.docs.map(docToMessage));
        setMessagesLoading(false);
      },
      (e) => {
        setError(e.message);
        setMessagesLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, opportunityId]);

  const threads = useMemo(() => {
    const legacy = buildLegacyOpportunityThread(userId, opportunityId, allMessages);
    return sortOpportunityChatThreadsRecentFirst(
      legacy ? [...storedThreads, legacy] : storedThreads
    );
  }, [allMessages, opportunityId, storedThreads, userId]);

  const messages = useMemo(
    () => sortOpportunityChatChronological(filterOpportunityThreadMessages(allMessages, activeThreadId)),
    [activeThreadId, allMessages]
  );

  const createThread = useCallback(
    async (title = 'New chat'): Promise<OpportunityChatThread> => {
      if (!userId) {
        throw new Error('Sign in to start a chat');
      }

      const now = new Date().toISOString();
      const thread: OpportunityChatThread = {
        id: newId(),
        userId,
        opportunityId,
        title: title.trim() || 'New chat',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(
        doc(db, COLLECTION_OPPORTUNITY_CHAT_THREADS, ownedItemDocId(userId, thread.id)),
        thread
      );

      return thread;
    },
    [opportunityId, userId]
  );

  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      if (!userId) {
        throw new Error('Sign in to delete a chat');
      }

      const isLegacy = threadId === OPPORTUNITY_LEGACY_THREAD_ID;
      const messagesQuery = isLegacy
        ? query(
            collection(db, COLLECTION_OPPORTUNITY_CHAT_MESSAGES),
            where('userId', '==', userId),
            where('opportunityId', '==', opportunityId)
          )
        : query(
            collection(db, COLLECTION_OPPORTUNITY_CHAT_MESSAGES),
            where('userId', '==', userId),
            where('opportunityId', '==', opportunityId),
            where('threadId', '==', threadId)
          );

      const messageSnap = await getDocs(messagesQuery);
      const messageRefs = messageSnap.docs
        .filter((messageDoc) => !isLegacy || typeof messageDoc.data().threadId !== 'string')
        .map((messageDoc) => messageDoc.ref);

      const refsToDelete: DocumentReference[] = [...messageRefs];
      if (!isLegacy) {
        refsToDelete.push(
          doc(db, COLLECTION_OPPORTUNITY_CHAT_THREADS, ownedItemDocId(userId, threadId))
        );
      }

      for (let i = 0; i < refsToDelete.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        for (const ref of refsToDelete.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
          batch.delete(ref);
        }
        await batch.commit();
      }
    },
    [opportunityId, userId]
  );

  return useMemo(
    () => ({
      threads,
      messages,
      threadsLoading,
      messagesLoading,
      error,
      createThread,
      deleteThread,
    }),
    [threads, messages, threadsLoading, messagesLoading, error, createThread, deleteThread]
  );
}
